import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { notifications } from "@mantine/notifications";

import type { GstBasis } from "@/components/GstTag";
import { grossUpCents } from "@/lib/gst";

interface GstContextValue {
  /** Current viewing lens: true = show excl figures grossed up to incl. */
  inclusive: boolean;
  /** Flip the lens; persists to the user's preference in the background. */
  setInclusive: (next: boolean) => void;
  /**
   * Convert a canonical amount for display under the current lens. Only
   * canonical-`excl` figures move (and only when the lens is on); canonical
   * `incl` cash figures are always the real amount and pass through.
   */
  displayCents: (cents: number, canonicalBasis: GstBasis) => number;
  /** The basis a figure should be *labelled* with, given its canonical basis. */
  displayBasis: (canonicalBasis: GstBasis) => GstBasis;
}

// Identity lens — used when no provider is mounted (e.g. isolated component
// tests). Renders every figure as its canonical self.
const IDENTITY: GstContextValue = {
  inclusive: false,
  setInclusive: () => {},
  displayCents: (cents) => cents,
  displayBasis: (basis) => basis,
};

const GstContext = createContext<GstContextValue | null>(null);

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
}

interface GstProviderProps {
  initialInclusive: boolean;
  children: ReactNode;
}

/**
 * Holds the GST display lens for the whole SPA session. Seeded from the
 * signed-in user's stored preference and flipped optimistically: the client
 * grosses figures itself, so no server round-trip or page reload is needed to
 * re-render — the PATCH only records the choice for the next full page load.
 */
export function GstProvider({ initialInclusive, children }: GstProviderProps) {
  const [inclusive, setInclusiveState] = useState(initialInclusive);

  const setInclusive = useCallback((next: boolean) => {
    setInclusiveState(next); // optimistic — the lens applies immediately

    void fetch("/user_preference", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
      body: JSON.stringify({ user_preference: { gst_inclusive: next } }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
      })
      .catch(() => {
        setInclusiveState(!next); // roll back the optimistic flip
        notifications.show({
          color: "red",
          message: "Couldn’t save your GST display preference. Please try again.",
        });
      });
  }, []);

  const value: GstContextValue = {
    inclusive,
    setInclusive,
    displayCents: (cents, canonicalBasis) =>
      inclusive && canonicalBasis === "excl" ? grossUpCents(cents) : cents,
    displayBasis: (canonicalBasis) =>
      inclusive && canonicalBasis === "excl" ? "incl" : canonicalBasis,
  };

  return <GstContext.Provider value={value}>{children}</GstContext.Provider>;
}

export function useGst(): GstContextValue {
  return useContext(GstContext) ?? IDENTITY;
}
